// components/SteinDetail.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import API from '../services/api';
import BilderGalerie from './BilderGalerie';
import '../styles/SteinDetail.css';
import '../styles/Form.css';
import '../styles/BilderGalerie.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faEdit, faTrash, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const KATEGORIEN = ['Einzelscheiben', 'Serien', 'Längsschnitte', 'Kristalle'];

const initialSteinState = {
  name: '', farbe: '', merkmal: '', kategorie: '',
  laenge: null, breite: null, tiefe: null,
  aufGlas: false, dicke: null,
  gewicht: null, preisProGramm: null,
  serienBezeichnung: '',
  status: 'potenzieller Kauf', kaufPreis: null, kaufDatum: '',
  verkaufOrt: '', kaeufer: '', notizen: '', beschreibung: '', bilder: [],
  kaufOrt: '', verkaeufer: '', verkaufPreis: null, verkaufDatum: '',
};

function SteinDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const isNewMode = location.pathname === '/steine/new';
  const initialEditMode = queryParams.get('edit') === 'true' || isNewMode;

  const [stein, setStein] = useState(isNewMode ? initialSteinState : null);
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [isLoading, setIsLoading] = useState(!isNewMode && id);
  const [error, setError] = useState(null);
  const userRole = localStorage.getItem('role') || 'guest';
  const [serienBezeichnungOptions, setSerienBezeichnungOptions] = useState([]);
  const [showNewSerienBezeichnung, setShowNewSerienBezeichnung] = useState(false);
  const [neueBilderDateien, setNeueBilderDateien] = useState([]);
  const [anzeigeName, setAnzeigeName] = useState('');
  const [anzeigeNameLoaded, setAnzeigeNameLoaded] = useState(false);

  useEffect(() => {
    if (userRole === 'guest') {
      navigate('/');
    } else if (isNewMode && userRole !== 'admin') {
      alert('Sie haben keine Berechtigung, einen neuen Stein zu erstellen.');
      navigate('/');
    }
  }, [userRole, isNewMode, navigate]);

  const fetchStein = useCallback((forceRefetch = false) => {
    if ((isNewMode && !forceRefetch) || userRole === 'guest') return;

    const targetId = forceRefetch ? (stein?._id || id) : id;
    if (!targetId) return;

    setIsLoading(true);
    setError(null);
    API.get(`/steine/${targetId}`)
      .then((res) => {
        const d = res.data;
        const fetchedData = {
          ...initialSteinState,
          ...d,
          kaufDatum: d.kaufDatum ? new Date(d.kaufDatum).toISOString().substring(0, 10) : '',
          verkaufDatum: d.verkaufDatum ? new Date(d.verkaufDatum).toISOString().substring(0, 10) : '',
          laenge: d.laenge ?? '',
          breite: d.breite ?? '',
          tiefe: d.tiefe ?? '',
          dicke: d.dicke ?? '',
          aufGlas: Boolean(d.aufGlas),
          gewicht: d.gewicht ?? '',
          preisProGramm: d.preisProGramm ?? '',
          kaufPreis: d.kaufPreis ?? '',
          verkaufPreis: d.verkaufPreis ?? '',
          serienBezeichnung: d.serienBezeichnung ?? '',
          kategorie: d.kategorie ?? '',
          merkmal: d.merkmal ?? '',
          kaufOrt: d.kaufOrt ?? '',
          verkaeufer: d.verkaeufer ?? '',
          verkaufOrt: d.verkaufOrt ?? '',
          kaeufer: d.kaeufer ?? '',
          notizen: d.notizen ?? '',
          beschreibung: d.beschreibung ?? '',
          farbe: d.farbe ?? '',
          bilder: d.bilder ?? [],
        };
        setStein(fetchedData);
        setNeueBilderDateien([]);
        if (forceRefetch) {
          setIsEditing(false);
          navigate(`/steine/${targetId}`, { replace: true });
        }
      })
      .catch((err) => {
        console.error('Fehler beim Laden des Steins:', err);
        setError(`Stein mit ID ${targetId} konnte nicht geladen werden.`);
        if (!forceRefetch) navigate('/');
      })
      .finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, userRole, navigate, stein?._id]);

  const fetchSerienBezeichnungen = useCallback(() => {
    API.get('/steine/serienBezeichnungen')
      .then((res) => setSerienBezeichnungOptions(res.data.filter(Boolean).sort()))
      .catch((err) => console.error('Fehler beim Laden der Serienbezeichnungen:', err));
  }, []);

  useEffect(() => { fetchStein(); }, [id, fetchStein]);
  useEffect(() => { fetchSerienBezeichnungen(); }, [fetchSerienBezeichnungen]);

  useEffect(() => {
    if (isNewMode || !id || anzeigeNameLoaded) return;
    API.get(`/steine/${id}/showcase`)
      .then((res) => { setAnzeigeName(res.data.anzeigeName || ''); setAnzeigeNameLoaded(true); })
      .catch(() => setAnzeigeNameLoaded(true));
  }, [id, isNewMode, anzeigeNameLoaded]);

  const handleAnzeigeNameBlur = async () => {
    if (isNewMode || !id || userRole !== 'admin') return;
    try { await API.patch(`/steine/${id}/showcase`, { anzeigeName }); } catch (err) {
      console.error('Fehler beim Speichern des Anzeigenamens:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setStein((prev) => {
      let newValue;
      if (type === 'checkbox') newValue = checked;
      else if (type === 'number') newValue = value;
      else if (type === 'date') newValue = value || '';
      else newValue = value;

      const newState = { ...prev, [name]: newValue };

      // Wenn Kategorie wechselt, kategorieabhängige Felder leeren
      if (name === 'kategorie') {
        if (newValue === 'Kristalle') {
          newState.aufGlas = false;
          newState.dicke = '';
        } else {
          newState.tiefe = '';
        }
        // Serien-Felder leeren wenn nicht Serien
        if (newValue !== 'Serien') {
          newState.serienBezeichnung = '';
          setShowNewSerienBezeichnung(false);
        }
      }

      // aufGlas aktiviert → dicke leeren
      if (name === 'aufGlas' && checked) {
        newState.dicke = '';
      }

      if (name === 'serienBezeichnung' && value === 'neu') {
        newState.serienBezeichnung = '';
      }

      return newState;
    });

    if (name === 'serienBezeichnung') {
      setShowNewSerienBezeichnung(value === 'neu');
    }
  };

  const handleNeueBilderHinzufuegen = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setNeueBilderDateien(prev => [...prev, ...Array.from(e.target.files)]);
      e.target.value = '';
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    setError(null);

    if (!stein.name || stein.name.trim() === '') {
      const msg = 'Der Name ist ein Pflichtfeld.';
      setError(msg); alert(msg); return;
    }

    if (stein.kategorie === 'Serien' && showNewSerienBezeichnung && (!stein.serienBezeichnung || stein.serienBezeichnung.trim() === '')) {
      const msg = 'Bitte geben Sie die neue Serienbezeichnung ein oder wählen Sie eine bestehende aus.';
      setError(msg); alert(msg); return;
    }

    setIsLoading(true);
    const formData = new FormData();
    const dataToSend = { ...stein };

    Object.keys(dataToSend).forEach((key) => {
      if (['bilder', '_id', '__v', 'bildPfad', 'kennzeichen', 'durchmesser'].includes(key)) return;

      let value = dataToSend[key];

      if (['laenge', 'breite', 'tiefe', 'dicke', 'gewicht', 'preisProGramm', 'kaufPreis', 'verkaufPreis'].includes(key)) {
        value = (value === '' || value === null || value === undefined) ? null : parseFloat(value);
        if (isNaN(value)) value = null;
      } else if (['kaufDatum', 'verkaufDatum'].includes(key)) {
        try {
          value = value ? new Date(value).toISOString() : null;
          if (value && isNaN(new Date(value).getTime())) value = null;
        } catch { value = null; }
      } else if (key === 'aufGlas') {
        value = Boolean(value);
      } else if (typeof value === 'string' && value.trim() === '') {
        value = null;
      }

      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    neueBilderDateien.forEach(datei => formData.append('neueBilder', datei));

    const apiCall = isNewMode
      ? API.post('/steine', formData)
      : API.put(`/steine/${id}`, formData);

    apiCall
      .then((res) => {
        if (isNewMode) {
          alert('Stein erfolgreich erstellt!');
          navigate(`/steine/${res.data._id}`, { replace: true });
        } else {
          alert('Stein erfolgreich aktualisiert!');
          fetchStein(true);
        }
      })
      .catch((err) => {
        console.error('Fehler beim Speichern des Steins:', err.response?.data || err.message);
        const errorMsg = err.response?.data?.message || err.response?.data || `Fehler beim Speichern: ${err.message}`;
        setError(errorMsg);
        alert(`Fehler beim Speichern: ${errorMsg}`);
      })
      .finally(() => setIsLoading(false));
  };

  const handleDelete = () => {
    if (isNewMode) return;
    if (window.confirm('Sind Sie sicher, dass Sie diesen Stein unwiderruflich löschen möchten?')) {
      setIsLoading(true); setError(null);
      API.delete(`/steine/${id}`)
        .then(() => { alert('Stein erfolgreich gelöscht.'); navigate('/'); })
        .catch((err) => {
          const errorMsg = err.response?.data?.message || 'Fehler beim Löschen des Steins.';
          setError(errorMsg); alert(errorMsg); setIsLoading(false);
        });
    }
  };

  const handleEdit = () => {
    if (userRole === 'admin') {
      setIsEditing(true);
      navigate('?edit=true', { replace: true, state: location.state });
    } else {
      alert('Sie haben keine Berechtigung zum Bearbeiten.');
    }
  };

  const handleCancelEdit = () => {
    setError(null); setNeueBilderDateien([]);
    if (isNewMode) { navigate('/'); } else {
      fetchStein(); setIsEditing(false);
      navigate(`/steine/${id}`, { replace: true });
    }
    setShowNewSerienBezeichnung(false);
  };

  const handleBack = () => navigate('/');

  if ((userRole === 'guest') || (isNewMode && userRole !== 'admin')) return null;
  if (isLoading && !stein && !isNewMode) return <main><div className="loading-message">Laden...</div></main>;
  if (error && !isEditing && !isNewMode) return (
    <main>
      <div className="error-message">{error}</div>
      <button onClick={handleBack} className="btn btn-back"><FontAwesomeIcon icon={faArrowLeft} /> Zurück zur Liste</button>
    </main>
  );
  if (!stein && !isNewMode) return <main><div className="error-message">Stein nicht gefunden.</div></main>;

  const canEdit = userRole === 'admin';
  const istKristall = stein?.kategorie === 'Kristalle';
  const istSerie = stein?.kategorie === 'Serien';

  return (
    <main>
      <div className="stein-detail">
        {isEditing ? (
          <>
            <h2>{isNewMode ? 'Neuen Stein erstellen' : 'Stein bearbeiten'}</h2>
            {error && <p className="error-message">{error}</p>}
            <form className="stein-form" onSubmit={handleSave}>
              <div className="form-grid">

                <fieldset className="form-section">
                  <legend>Basisinformationen</legend>
                  {stein.kennzeichen && (
                    <div className="form-group">
                      <label>Kennzeichen:</label>
                      <span className="readonly-value kennzeichen-badge">{stein.kennzeichen}</span>
                    </div>
                  )}
                  <div className="form-group">
                    <label htmlFor="name">Name<span className="required">*</span>:</label>
                    <input type="text" id="name" name="name" value={stein.name || ''} onChange={handleChange} required disabled={isLoading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="kategorie">Kategorie:</label>
                    <select id="kategorie" name="kategorie" value={stein.kategorie || ''} onChange={handleChange} disabled={isLoading}>
                      <option value="">— Bitte wählen —</option>
                      {KATEGORIEN.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="merkmal">Merkmal der Steine:</label>
                    <input type="text" id="merkmal" name="merkmal" value={stein.merkmal || ''} onChange={handleChange} disabled={isLoading} placeholder="z. B. Rubellit, Indigolit…" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="farbe">Farbe:</label>
                    <input type="text" id="farbe" name="farbe" value={stein.farbe || ''} onChange={handleChange} disabled={isLoading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="status">Status:</label>
                    <select id="status" name="status" value={stein.status || ''} onChange={handleChange} disabled={isLoading}>
                      <option value="potenzieller Kauf">Potenzieller Kauf</option>
                      <option value="gekauft">Gekauft</option>
                      <option value="verkauft">Verkauft</option>
                      <option value="noch keinen Kontakt">Noch keinen Kontakt</option>
                    </select>
                  </div>
                </fieldset>

                {/* Serien-Bereich */}
                {istSerie && (
                  <fieldset className="form-section">
                    <legend>Serie</legend>
                    <div className="form-group">
                      <label htmlFor="serienBezeichnung">Serienbezeichnung:</label>
                      <select
                        id="serienBezeichnung"
                        name="serienBezeichnung"
                        value={showNewSerienBezeichnung ? 'neu' : (stein.serienBezeichnung || '')}
                        onChange={handleChange}
                        disabled={isLoading}
                      >
                        <option value="" disabled={!showNewSerienBezeichnung}>Bitte wählen oder neu hinzufügen</option>
                        {serienBezeichnungOptions.map(s => <option key={s} value={s}>{s}</option>)}
                        <option value="neu" className="new-serien-option">— Neue hinzufügen —</option>
                      </select>
                    </div>
                    {showNewSerienBezeichnung && (
                      <div className="form-group">
                        <label htmlFor="neueSerienBezeichnungInput">Neue Serienbez.:</label>
                        <input
                          type="text"
                          id="neueSerienBezeichnungInput"
                          name="serienBezeichnung"
                          value={stein.serienBezeichnung || ''}
                          onChange={handleChange}
                          placeholder="Name der neuen Serie"
                          disabled={isLoading}
                          required
                        />
                      </div>
                    )}
                  </fieldset>
                )}

                <fieldset className="form-section">
                  <legend>Maße &amp; Gewicht</legend>

                  {/* Kristalle: Länge, Breite, Tiefe */}
                  {istKristall ? (
                    <>
                      <div className="form-group">
                        <label htmlFor="laenge">Länge (mm):</label>
                        <input type="number" step="0.01" id="laenge" name="laenge" value={stein.laenge ?? ''} onChange={handleChange} disabled={isLoading} />
                      </div>
                      <div className="form-group">
                        <label htmlFor="breite">Breite (mm):</label>
                        <input type="number" step="0.01" id="breite" name="breite" value={stein.breite ?? ''} onChange={handleChange} disabled={isLoading} />
                      </div>
                      <div className="form-group">
                        <label htmlFor="tiefe">Tiefe (mm):</label>
                        <input type="number" step="0.01" id="tiefe" name="tiefe" value={stein.tiefe ?? ''} onChange={handleChange} disabled={isLoading} />
                      </div>
                    </>
                  ) : (
                    /* Alle anderen: Länge, Breite, Auf Glas / Dicke */
                    <>
                      <div className="form-group">
                        <label htmlFor="laenge">Länge (mm):</label>
                        <input type="number" step="0.01" id="laenge" name="laenge" value={stein.laenge ?? ''} onChange={handleChange} disabled={isLoading} />
                      </div>
                      <div className="form-group">
                        <label htmlFor="breite">Breite (mm):</label>
                        <input type="number" step="0.01" id="breite" name="breite" value={stein.breite ?? ''} onChange={handleChange} disabled={isLoading} />
                      </div>
                      <div className="form-group checkbox-group">
                        <label htmlFor="aufGlas">
                          <input type="checkbox" id="aufGlas" name="aufGlas" checked={Boolean(stein.aufGlas)} onChange={handleChange} disabled={isLoading} />
                          Auf Glas (keine Dickenangabe nötig)
                        </label>
                      </div>
                      {!stein.aufGlas && (
                        <div className="form-group">
                          <label htmlFor="dicke">Dicke der Scheibe (mm):</label>
                          <input type="number" step="0.01" id="dicke" name="dicke" value={stein.dicke ?? ''} onChange={handleChange} disabled={isLoading} />
                        </div>
                      )}
                    </>
                  )}

                  {/* Legacy durchmesser — nur Anzeige wenn vorhanden */}
                  {stein.durchmesser != null && stein.durchmesser !== '' && (
                    <div className="form-group">
                      <label>Durchmesser (mm) <small>[Legacy]</small>:</label>
                      <span className="readonly-value">{stein.durchmesser}</span>
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="gewicht">Gewicht (g):</label>
                    <input type="number" step="0.01" id="gewicht" name="gewicht" value={stein.gewicht ?? ''} onChange={handleChange} disabled={isLoading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="preisProGramm">Preis pro Gramm (€):</label>
                    <input type="number" step="0.01" id="preisProGramm" name="preisProGramm" value={stein.preisProGramm ?? ''} onChange={handleChange} disabled={isLoading} />
                  </div>
                </fieldset>

                <fieldset className="form-section details-fieldset">
                  <legend>Kaufdetails</legend>
                  <div className="form-group">
                    <label htmlFor="kaufPreis">Kaufpreis (€):</label>
                    <input type="number" step="0.01" id="kaufPreis" name="kaufPreis" value={stein.kaufPreis ?? ''} onChange={handleChange} disabled={isLoading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="kaufDatum">Kaufdatum:</label>
                    <input type="date" id="kaufDatum" name="kaufDatum" value={stein.kaufDatum || ''} onChange={handleChange} disabled={isLoading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="kaufOrt">Kaufort:</label>
                    <input type="text" id="kaufOrt" name="kaufOrt" value={stein.kaufOrt || ''} onChange={handleChange} disabled={isLoading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="verkaeufer">Verkäufer:</label>
                    <input type="text" id="verkaeufer" name="verkaeufer" value={stein.verkaeufer || ''} onChange={handleChange} disabled={isLoading} />
                  </div>
                </fieldset>

                <fieldset className="form-section details-fieldset">
                  <legend>Verkaufsdetails</legend>
                  <div className="form-group">
                    <label htmlFor="verkaufPreis">Verkaufspreis (€):</label>
                    <input type="number" step="0.01" id="verkaufPreis" name="verkaufPreis" value={stein.verkaufPreis ?? ''} onChange={handleChange} disabled={isLoading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="verkaufDatum">Verkaufsdatum:</label>
                    <input type="date" id="verkaufDatum" name="verkaufDatum" value={stein.verkaufDatum || ''} onChange={handleChange} disabled={isLoading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="verkaufOrt">Verkaufsort:</label>
                    <input type="text" id="verkaufOrt" name="verkaufOrt" value={stein.verkaufOrt || ''} onChange={handleChange} disabled={isLoading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="kaeufer">Käufer:</label>
                    <input type="text" id="kaeufer" name="kaeufer" value={stein.kaeufer || ''} onChange={handleChange} disabled={isLoading} />
                  </div>
                </fieldset>

                <fieldset className="form-section form-section-full-width">
                  <legend>Notizen &amp; Bilder</legend>
                  <div className="form-group">
                    <label htmlFor="beschreibung">Öffentliche Beschreibung <small style={{ fontWeight: 'normal', color: '#888' }}>(erscheint auf turmalin-finder.de)</small>:</label>
                    <textarea id="beschreibung" name="beschreibung" value={stein.beschreibung || ''} onChange={handleChange} rows="4" disabled={isLoading} placeholder="Kurze Beschreibung des Steins für die öffentliche Webseite…" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="anzeigeName">Schaufenster-Name <small style={{ fontWeight: 'normal', color: '#888' }}>(optionaler Name auf turmalin-finder.de)</small>:</label>
                    <input
                      type="text"
                      id="anzeigeName"
                      value={anzeigeName}
                      onChange={(e) => setAnzeigeName(e.target.value)}
                      onBlur={handleAnzeigeNameBlur}
                      placeholder={stein.name || ''}
                      disabled={isLoading || isNewMode || userRole !== 'admin'}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="notizen">Notizen <small style={{ fontWeight: 'normal', color: '#888' }}>(intern, nicht öffentlich)</small>:</label>
                    <textarea id="notizen" name="notizen" value={stein.notizen || ''} onChange={handleChange} rows="5" disabled={isLoading} />
                  </div>
                  <div className="form-group">
                    <label>Vorhandene Bilder:</label>
                    <BilderGalerie
                      bilder={stein.bilder || []}
                      neueDateien={neueBilderDateien}
                      steinId={isNewMode ? null : id}
                      bearbeitungsModus={true}
                      onBilderChange={(neueBilder) => setStein(prev => ({ ...prev, bilder: neueBilder }))}
                      onNeueDateienChange={(dateien) => setNeueBilderDateien(dateien)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="bilder-upload-label" htmlFor="neueBilderInput">
                      &#43; Weitere Bilder hinzufügen
                      <input
                        type="file"
                        id="neueBilderInput"
                        accept="image/jpeg,image/jpg,image/png"
                        multiple
                        className="bilder-upload-input"
                        onChange={handleNeueBilderHinzufuegen}
                        disabled={isLoading}
                      />
                    </label>
                    <small style={{ display: 'block', marginTop: '0.3rem', color: '#777' }}>JPEG/JPG/PNG, max. 5 MB pro Bild</small>
                  </div>
                </fieldset>
              </div>

              <div className="button-group form-actions">
                <button type="submit" className="btn btn-submit" disabled={isLoading}>
                  <FontAwesomeIcon icon={faSave} /> {isLoading ? 'Speichern…' : 'Speichern'}
                </button>
                <button type="button" className="btn btn-cancel" onClick={handleCancelEdit} disabled={isLoading}>
                  <FontAwesomeIcon icon={faTimes} /> Abbrechen
                </button>
                {id !== 'new' && canEdit && (
                  <button type="button" onClick={handleDelete} className="btn btn-delete" disabled={isLoading}>
                    <FontAwesomeIcon icon={faTrash} /> Löschen
                  </button>
                )}
              </div>
            </form>
          </>
        ) : (
          <>
            <h2>{stein.name || 'Unbenannter Stein'}</h2>
            {stein.kennzeichen && (
              <p className="kennzeichen-display"><strong>Kennzeichen:</strong> <span className="kennzeichen-badge">{stein.kennzeichen}</span></p>
            )}
            <BilderGalerie bilder={stein.bilder || []} neueDateien={[]} bearbeitungsModus={false} />

            <div className="stein-info">
              {renderDetailField('Kategorie', stein.kategorie)}
              {renderDetailField('Merkmal der Steine', stein.merkmal)}
              {renderDetailField('Farbe', stein.farbe)}
              {renderDetailField('Status', stein.status)}

              {/* Maße je nach Kategorie */}
              {stein.kategorie === 'Kristalle' ? (
                <>
                  {renderDetailField('Länge (mm)', stein.laenge)}
                  {renderDetailField('Breite (mm)', stein.breite)}
                  {renderDetailField('Tiefe (mm)', stein.tiefe)}
                </>
              ) : (
                <>
                  {renderDetailField('Länge (mm)', stein.laenge)}
                  {renderDetailField('Breite (mm)', stein.breite)}
                  {stein.aufGlas
                    ? renderDetailField('Montierung', 'Auf Glas')
                    : renderDetailField('Dicke der Scheibe (mm)', stein.dicke)}
                </>
              )}
              {stein.durchmesser != null && renderDetailField('Durchmesser (mm) [Legacy]', stein.durchmesser)}

              {renderDetailField('Gewicht (g)', stein.gewicht)}
              {renderDetailField('Preis pro Gramm (€)', stein.preisProGramm)}

              {stein.kategorie === 'Serien' && renderDetailField('Serienbezeichnung', stein.serienBezeichnung)}

              {(stein.kaufPreis || stein.kaufDatum || stein.kaufOrt || stein.verkaeufer) && (
                <div className="info-section">
                  <h3>Kaufdetails</h3>
                  {renderDetailField('Kaufpreis (€)', stein.kaufPreis)}
                  {renderDetailField('Kaufdatum', stein.kaufDatum ? new Date(stein.kaufDatum).toLocaleDateString('de-DE') : null)}
                  {renderDetailField('Kaufort', stein.kaufOrt)}
                  {renderDetailField('Verkäufer', stein.verkaeufer)}
                </div>
              )}

              {(stein.verkaufPreis || stein.verkaufDatum || stein.verkaufOrt || stein.kaeufer) && (
                <div className="info-section">
                  <h3>Verkaufsdetails</h3>
                  {renderDetailField('Verkaufspreis (€)', stein.verkaufPreis)}
                  {renderDetailField('Verkaufsdatum', stein.verkaufDatum ? new Date(stein.verkaufDatum).toLocaleDateString('de-DE') : null)}
                  {renderDetailField('Verkaufsort', stein.verkaufOrt)}
                  {renderDetailField('Käufer', stein.kaeufer)}
                </div>
              )}

              {stein.notizen && (
                <div className="info-section"><h3>Notizen</h3><p className="notizen-text">{stein.notizen}</p></div>
              )}
              {stein.beschreibung && (
                <div className="info-section"><h3>Öffentliche Beschreibung</h3><p className="notizen-text">{stein.beschreibung}</p></div>
              )}
              {anzeigeName && (
                <div className="info-section"><h3>Schaufenster-Name</h3><p>{anzeigeName}</p></div>
              )}
            </div>

            <div className="button-group view-actions">
              <button onClick={handleBack} className="btn btn-back">
                <FontAwesomeIcon icon={faArrowLeft} /> Zurück zur Liste
              </button>
              {canEdit && (
                <>
                  <button onClick={handleEdit} className="btn btn-edit"><FontAwesomeIcon icon={faEdit} /> Bearbeiten</button>
                  <button onClick={handleDelete} className="btn btn-delete"><FontAwesomeIcon icon={faTrash} /> Löschen</button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}

function renderDetailField(label, value) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <p><strong>{label}:</strong> {value}</p>
  );
}

export default SteinDetail;
